<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import AppNavIcon, { type NavIconName } from '@/components/app-nav-icon.vue';
import AppSiteFooter from '@/components/app-site-footer.vue';
import { useApprovalsStore } from '@/store/use-approvals-store';
import { useAuthStore } from '@/store/use-auth-store';

type NavItem = {
  to: string;
  label: string;
  icon: NavIconName;
  isExact?: boolean;
  badge?: number;
};

const COLLAPSED_KEY = 'sales.navCollapsed';
const ROLE_LABELS = {
  owner: 'Owner',
  sales_manager: 'Sales Manager',
  sales_rep: 'Sales Rep',
} as const;

const authStore = useAuthStore();
const approvalsStore = useApprovalsStore();
const route = useRoute();
const router = useRouter();

const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
};

const isCollapsed = ref(readCollapsed());
const isMenuOpen = ref(false);

const toggleCollapsed = () => {
  isCollapsed.value = !isCollapsed.value;
  try {
    localStorage.setItem(COLLAPSED_KEY, String(isCollapsed.value));
  } catch {
    return;
  }
};

const groups = computed<{ label: string | null; items: NavItem[] }[]>(() => {
  const admin: NavItem[] = [
    ...(authStore.canApproveDiscounts
      ? [
          {
            to: '/approvals',
            label: 'Approvals',
            icon: 'approvals' as const,
            badge: approvalsStore.items.length,
          },
        ]
      : []),
    ...(authStore.canManageTemplates
      ? [
          {
            to: '/order-form-template',
            label: 'Template',
            icon: 'template' as const,
          },
        ]
      : []),
    ...(authStore.canManageUsers
      ? [{ to: '/users', label: 'Users', icon: 'users' as const }]
      : []),
  ];
  return [
    {
      label: null,
      items: [{ to: '/', label: 'Home', icon: 'home', isExact: true }],
    },
    {
      label: 'Selling',
      items: [
        { to: '/rate-card', label: 'Rate card', icon: 'rate-card' },
        { to: '/quotes', label: 'Quotes', icon: 'quotes' },
        { to: '/order-forms', label: 'Order Forms', icon: 'order-forms' },
        { to: '/activities', label: 'Activities', icon: 'activities' },
      ],
    },
    {
      label: 'Money',
      items: [
        { to: '/commissions', label: 'Commissions', icon: 'commissions' },
        ...(authStore.canViewFinancials
          ? [
              {
                to: '/financials',
                label: 'Financials',
                icon: 'financials' as const,
              },
            ]
          : []),
      ],
    },
    ...(admin.length > 0 ? [{ label: 'Admin', items: admin }] : []),
  ];
});

const displayName = computed(
  () => authStore.user?.name ?? authStore.user?.email ?? '',
);
const initials = computed(() =>
  displayName.value
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join(''),
);
const roleLabel = computed(() =>
  authStore.user ? ROLE_LABELS[authStore.user.role] : '',
);

watch(
  () => route.fullPath,
  () => {
    isMenuOpen.value = false;
    if (authStore.canApproveDiscounts) {
      void approvalsStore.refresh().catch(() => undefined);
    }
  },
  { immediate: true },
);

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    isMenuOpen.value = false;
  }
};

onMounted(() => window.addEventListener('keydown', handleKeydown));
onBeforeUnmount(() => window.removeEventListener('keydown', handleKeydown));

const handleSignOut = async () => {
  await authStore.signOut();
  await router.push({ name: 'login' });
};

const ITEM_CLASS =
  'flex min-h-11 items-center gap-3 rounded-[10px] px-3 text-[0.90625rem] font-semibold text-[#DBE1EB] hover:bg-[rgba(219,225,235,0.08)] hover:text-white [&>svg]:text-[#94A3B8]';
const ACTIVE_CLASS =
  '!bg-[rgba(66,125,255,0.2)] !text-white [&>svg]:!text-[#7AABFF]';
const GROUP_LABEL_CLASS =
  'mx-3 mt-[1.125rem] mb-1.5 text-[0.6875rem] font-bold tracking-[0.09em] text-[#94A3B8] uppercase';
</script>

<template>
  <div class="flex min-h-screen bg-surface">
    <aside
      class="sticky top-0 hidden h-screen shrink-0 flex-col border-r border-[rgba(219,225,235,0.1)] bg-[#070B1F] py-4 lg:flex"
      :class="isCollapsed ? 'w-[4.5rem] items-center' : 'w-60 px-3'"
      aria-label="Sidebar"
    >
      <router-link
        to="/"
        class="flex items-center"
        :class="
          isCollapsed ? 'mb-3 h-12 w-12 justify-center' : 'px-2 pt-1 pb-3'
        "
      >
        <img
          v-if="isCollapsed"
          src="/icon-mark.png"
          alt="Exfluential Sales home"
          class="block h-9 w-9"
        />
        <img
          v-else
          src="/logo-on-dark.png"
          alt="Exfluential Sales home"
          class="block h-11 w-auto"
        />
      </router-link>

      <nav
        aria-label="Main"
        class="flex grow flex-col gap-0.5"
        :class="isCollapsed ? 'items-center' : 'overflow-y-auto'"
      >
        <template v-for="group in groups" :key="group.label ?? 'top'">
          <template v-if="group.label">
            <div
              v-if="isCollapsed"
              aria-hidden="true"
              class="my-2.5 h-px w-8 bg-[rgba(219,225,235,0.14)]"
            />
            <div v-else :class="GROUP_LABEL_CLASS">{{ group.label }}</div>
          </template>
          <router-link
            v-for="item in group.items"
            :key="item.to"
            :to="item.to"
            :exact-active-class="item.isExact ? ACTIVE_CLASS : undefined"
            :active-class="item.isExact ? undefined : ACTIVE_CLASS"
            :class="[
              ITEM_CLASS,
              isCollapsed ? 'group relative w-12 justify-center px-0' : '',
            ]"
            :aria-label="
              isCollapsed
                ? item.badge
                  ? `${item.label}, ${item.badge} waiting`
                  : item.label
                : undefined
            "
          >
            <AppNavIcon :name="item.icon" />
            <template v-if="!isCollapsed">
              <span class="grow">{{ item.label }}</span>
              <span
                v-if="item.badge"
                :aria-label="`${item.badge} waiting`"
                class="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#3FD0EA] px-1.5 text-xs font-bold text-[#070B1F]"
              >
                {{ item.badge }}
              </span>
            </template>
            <template v-else>
              <span
                v-if="item.badge"
                aria-hidden="true"
                class="absolute top-0.5 right-0.5 inline-flex h-[1.125rem] min-w-[1.125rem] items-center justify-center rounded-full border-2 border-[#070B1F] bg-[#3FD0EA] px-1 text-[0.6875rem] font-bold text-[#070B1F]"
              >
                {{ item.badge }}
              </span>
              <span
                aria-hidden="true"
                class="pointer-events-none absolute top-1/2 left-[3.75rem] z-10 hidden -translate-y-1/2 rounded-lg bg-[#1F2937] px-2.5 py-1.5 text-[0.8125rem] font-semibold whitespace-nowrap text-white shadow-lg group-hover:block group-focus-visible:block"
              >
                {{ item.label }}
              </span>
            </template>
          </router-link>
        </template>
      </nav>

      <div
        class="flex flex-col gap-1 border-t border-[rgba(219,225,235,0.12)] pt-3"
        :class="isCollapsed ? 'w-12 items-center' : ''"
      >
        <div
          v-if="!isCollapsed"
          class="flex items-center gap-2.5 py-1 pr-1 pl-2"
        >
          <span
            aria-hidden="true"
            class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#2F6BF0] text-xs font-bold text-white"
          >
            {{ initials }}
          </span>
          <div class="min-w-0 grow">
            <div class="truncate text-[0.8125rem] font-semibold text-[#EEF5F8]">
              {{ authStore.user?.email }}
            </div>
            <div class="text-xs text-[#94A3B8]">{{ roleLabel }}</div>
          </div>
          <button
            type="button"
            aria-label="Sign out"
            class="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-[10px] text-[#DBE1EB] hover:bg-[rgba(219,225,235,0.08)] hover:text-white"
            @click="handleSignOut"
          >
            <AppNavIcon name="sign-out" />
          </button>
        </div>
        <button
          v-else
          type="button"
          :aria-label="`Sign out ${authStore.user?.email ?? ''}`"
          :title="`Sign out ${authStore.user?.email ?? ''}`"
          class="flex h-11 w-11 cursor-pointer items-center justify-center rounded-[10px] text-[#DBE1EB] hover:bg-[rgba(219,225,235,0.08)] hover:text-white"
          @click="handleSignOut"
        >
          <AppNavIcon name="sign-out" />
        </button>
        <button
          type="button"
          :aria-label="isCollapsed ? 'Expand menu' : 'Collapse menu'"
          :aria-expanded="!isCollapsed"
          :class="[
            ITEM_CLASS,
            'cursor-pointer text-[0.84375rem]',
            isCollapsed ? 'w-12 justify-center px-0' : '',
          ]"
          @click="toggleCollapsed"
        >
          <AppNavIcon :name="isCollapsed ? 'expand' : 'collapse'" />
          <span v-if="!isCollapsed">Collapse menu</span>
        </button>
      </div>
    </aside>

    <div class="flex min-w-0 grow flex-col">
      <header
        class="sticky top-0 z-20 flex h-[3.75rem] items-center gap-2 bg-[#070B1F] pr-2 pl-1 lg:hidden"
      >
        <button
          type="button"
          aria-label="Open menu"
          :aria-expanded="isMenuOpen"
          aria-controls="mobile-menu"
          class="flex h-11 w-11 cursor-pointer items-center justify-center text-white"
          @click="isMenuOpen = true"
        >
          <AppNavIcon name="menu" :size="22" />
        </button>
        <router-link to="/" class="flex items-center">
          <img
            src="/logo-on-dark.png"
            alt="Exfluential Sales home"
            class="block h-9 w-auto"
          />
        </router-link>
      </header>

      <main class="mx-auto w-full max-w-6xl grow p-4 sm:p-8 lg:px-10">
        <router-view />
      </main>
      <AppSiteFooter />
    </div>

    <template v-if="isMenuOpen">
      <div
        aria-hidden="true"
        class="fixed inset-0 z-30 bg-[rgba(7,11,31,0.6)] lg:hidden"
        @click="isMenuOpen = false"
      />
      <aside
        id="mobile-menu"
        aria-label="Menu"
        class="fixed inset-y-0 left-0 z-40 flex w-[19rem] max-w-[85vw] flex-col bg-[#070B1F] px-3 pt-2 pb-4 shadow-2xl lg:hidden"
      >
        <div class="flex h-[3.25rem] items-center justify-between pl-1">
          <img
            src="/logo-on-dark.png"
            alt="Exfluential Sales"
            class="block h-9 w-auto"
          />
          <button
            type="button"
            aria-label="Close menu"
            class="flex h-11 w-11 cursor-pointer items-center justify-center text-white"
            @click="isMenuOpen = false"
          >
            <AppNavIcon name="close" :size="22" />
          </button>
        </div>
        <nav
          aria-label="Main"
          class="mt-2 flex grow flex-col gap-0.5 overflow-y-auto"
        >
          <template v-for="group in groups" :key="group.label ?? 'top'">
            <div
              v-if="group.label"
              :class="GROUP_LABEL_CLASS"
              class="!mt-3.5 !mb-1"
            >
              {{ group.label }}
            </div>
            <router-link
              v-for="item in group.items"
              :key="item.to"
              :to="item.to"
              :exact-active-class="item.isExact ? ACTIVE_CLASS : undefined"
              :active-class="item.isExact ? undefined : ACTIVE_CLASS"
              :class="ITEM_CLASS"
            >
              <AppNavIcon :name="item.icon" />
              <span class="grow">{{ item.label }}</span>
              <span
                v-if="item.badge"
                :aria-label="`${item.badge} waiting`"
                class="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#3FD0EA] px-1.5 text-xs font-bold text-[#070B1F]"
              >
                {{ item.badge }}
              </span>
            </router-link>
          </template>
        </nav>
        <div
          class="flex items-center gap-2.5 border-t border-[rgba(219,225,235,0.12)] pt-3 pl-2"
        >
          <span
            aria-hidden="true"
            class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#2F6BF0] text-xs font-bold text-white"
          >
            {{ initials }}
          </span>
          <div class="min-w-0 grow">
            <div class="truncate text-[0.8125rem] font-semibold text-[#EEF5F8]">
              {{ authStore.user?.email }}
            </div>
            <div class="text-xs text-[#94A3B8]">{{ roleLabel }}</div>
          </div>
          <button
            type="button"
            aria-label="Sign out"
            class="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-[10px] text-[#DBE1EB]"
            @click="handleSignOut"
          >
            <AppNavIcon name="sign-out" />
          </button>
        </div>
      </aside>
    </template>
  </div>
</template>
